import { useEffect, useState } from 'react';

import { fetchGetBannerEnrichmentsService } from 'shared/services/fetchGetBannerEnrichmentsService';
import { type IBannerEnrichments } from 'shared/repositories/ad-banner/entities/get_banner_enrichments_v1/BannerEnrichments';
import { type TWithServerEnrichment } from 'shared/types/bannerContext';
import { useApplicationContext } from 'shared/utils/applicationContext';

const bannerEnrichmentsMap = new Map<number, IBannerEnrichments>();

export function useBannerEnrichments({
  objectId,
  objectType,
  dealType,
  useServerEnrichment,
}: TWithServerEnrichment): null | IBannerEnrichments {
  const applicationContext = useApplicationContext();

  /**
   * @description Только для значений useServerEnrichment === '1' делаем запрос на бекенд для обогащения данных
   */
  const isEnrichmentFromBackendEnabled = useServerEnrichment === '1';

  // Number (в отличие от parseInt) не парсит частично: '12abc' -> NaN, а не 12, поэтому на бэкенд не уйдёт битый id.
  const objectIdNumber = objectId ? Number(objectId) : NaN;
  const objectIdInt = Number.isInteger(objectIdNumber) ? objectIdNumber : null;

  const [bannerEnrichments, setBannerEnrichments] = useState<null | IBannerEnrichments>(() =>
    objectIdInt && isEnrichmentFromBackendEnabled ? (bannerEnrichmentsMap.get(objectIdInt) ?? null) : null,
  );

  useEffect(() => {
    if (isEnrichmentFromBackendEnabled && objectIdInt && !bannerEnrichments) {
      fetchGetBannerEnrichmentsService(applicationContext, { dealType, objectId: objectIdInt, objectType })
        .then(data => {
          if (!data.enrichments) {
            return;
          }

          /**
           * @description не даём кешу расти бесконечно; стратегия хранения для нас не важна,
           * поэтому при переполнении просто чистим его целиком (проверка именно здесь, на каждой записи).
           */
          if (bannerEnrichmentsMap.size > 100) {
            bannerEnrichmentsMap.clear();
          }

          bannerEnrichmentsMap.set(objectIdInt, data.enrichments);

          setBannerEnrichments(data.enrichments);
        })
        .catch(() => {
          // Обогащение необязательно: при ошибке баннер рендерится без него, причина уже залогирована в сервисе.
        });
    }
    /**
     * @description изменение данных не должно триггерить запрос
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return bannerEnrichments;
}
